import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns/registry';
import type { Columns } from '@/components/table/types';
import { AssetStatusComponent } from '@/sections/asset/components/badges/AssetStatus';
import { AttackSurfaceComponent } from '@/sections/asset/components/tabs/AttackSurfaceTab';
import type { Asset } from '@/types';

// AssetStatusComponent (icon + uppercase label) needs more space than shared status columns.
const ASSET_STATUS_COLUMN_WIDTH = 100;

/**
 * Builds column definitions for Asset entity tables
 *
 * Returns columns for:
 * - identifier: Primary asset identifier (bold purple) with DNS below
 * - port: Port number only
 * - service: Service name (mysql, sip, ssh)
 * - surface: Attack surface classification
 * - status: Asset status
 *
 * The identifier column uses NameColumn component to display:
 * - Asset identifier in bold purple text
 * - DNS field below in smaller gray text
 *
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildAssetColumns();
 * // Returns array with identifier, port, service, surface, and status columns
 * // Each column has defensive null checks returning '-' for missing values
 */
export function buildAssetColumns(): Columns<Asset> {
  return [
    {
      id: 'status',
      label: 'Asset Status',
      ...getColumnWidth('shared', 'status'),
      fixedWidth: ASSET_STATUS_COLUMN_WIDTH,
      minWidth: ASSET_STATUS_COLUMN_WIDTH,
      maxWidth: ASSET_STATUS_COLUMN_WIDTH,
      cell: (item: Asset) =>
        item?.status ? <AssetStatusComponent asset={item} /> : '-',
    },
    {
      id: 'identifier',
      label: 'Identifier',
      ...getColumnWidth('asset', 'identifier'),
      cell: (item: Asset) => {
        if (!item?.identifier) {
          return '-';
        }
        return <NameColumn name={item.identifier} description={item.dns} />;
      },
    },
    {
      id: 'port',
      label: 'Port',
      ...getColumnWidth('shared', 'port'),
      sorting: false,
      cell: (item: Asset) => {
        const portData = item?.ports?.[0];
        if (!portData?.port) return '-';
        return <span>{portData.port}</span>;
      },
    },
    {
      id: '',
      label: 'Service',
      ...getColumnWidth('shared', 'service'),
      sorting: false,
      cell: (item: Asset) => {
        const portData = item?.ports?.[0];
        if (!portData?.service) return '-';
        return <span>{portData.service}</span>;
      },
    },
    {
      id: 'attackSurface',
      label: 'Attack Surface',
      ...getColumnWidth('asset', 'attackSurface'),
      sorting: false,
      cell: (item: Asset) =>
        item?.attackSurface?.length ? (
          <AttackSurfaceComponent
            asset={item}
            maxVisible={1}
            size="xs"
            wrapInTooltip
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'created',
      label: 'First Seen',
      ...getColumnWidth('shared', 'created'),
      cell: 'date' as const,
    },
    {
      id: 'visited',
      label: 'Last Seen',
      ...getColumnWidth('shared', 'visited'),
      cell: 'date' as const,
    },
  ];
}
